import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ProjectGroupComponent } from '../../list/project-group/project-group.component';
import { toProjectVm, ProjectVm } from '../../list/list-grouping.util';
import { ProjectService } from '../../services/project.service';
import { ModalService } from '../../services/modal.service';
import { CommonUtilityServiceService } from '../../services/common-utility-service.service';
import { AppService } from '../../services/app.service';
import { SignedAtpSiteVisit } from '../../model/signed-atp-site-visit';

// Page-hosted confirmation dialogs (Spec 8, on <app-modal>) — a list/card
// refresh destroys the triggering row, so these live at the page level and
// are driven by ModalService, exactly like the Active/Invitations/Proposals
// pages (Spec 4).
import { AllBidsSubmittedDialogComponent } from '../../allbids-submitted-dialog/allbids-submitted-dialog.component';
import { BidSubmittedDialogComponent } from '../../bid-submitted-dialog/bid-submitted-dialog.component';
import { AtpSignAcceptedDialogComponent } from '../../atp-sign-accepted-dialog/atp-sign-accepted-dialog.component';
import { AcceptedOfferedProposalDialogComponent } from '../../accepted-offered-proposal-dialog/accepted-offered-proposal-dialog.component';
import { DeclineSuccessComponent } from '../../decline-success/decline-success.component';
import { BatchDeclineSummary } from '../../model/batch-decline';
import { SiteVisitConfirmationDialogComponent } from '../../site-visit/site-visit-confirmation-dialog/site-visit-confirmation-dialog.component';

/**
 * CONNECT 2.0 Project Overview (Spec 9, Task 1) — a single project shown
 * across every status (Invitation → Completed), built from the same nested
 * `app-project-group` / `app-site-card` / `app-product-line` cards the
 * Invitations/Proposals/Active list screens use (Spec 4). No new card
 * components — the project banner, per-site facts, and per-product action
 * matrix are 100% the reused Spec 4 render; this page only supplies the data
 * scope (one project, DECLINED dropped).
 *
 * CON-154 Task 5: the project-level bulk actions (Submit All Quotes / Accept
 * All Offers / Sign All ATPs) used to be hosted here as well
 * (Task 2's original placement), duplicating what `app-project-group` now
 * owns for every list screen. That page-local copy — handlers, gating flags,
 * and the three bulk ACTION dialog instances — has been removed; the banner
 * bound above serves this page's bulk actions the same way it serves
 * Invitations/Proposals/Active, and relays a signed ATP's site visits back up
 * through the existing `(atpSigned)` output.
 *
 * `type` passed to `<app-project-group>` is deliberately NOT `'active'`: that
 * value also (a) unconditionally renders project-group's "N products in
 * progress" + ATP-stamp banner block, which would misreport a project that's
 * mostly still at Invitation/Offered, and (b) unconditionally renders
 * site-card's "Open Workspace" deep-link for every site regardless of that
 * site's actual status. Both gates are keyed off the page-level `type` input
 * rather than each site/product's own status, so there is no `type` value
 * that both (1) shows the workspace deep-link only for genuinely
 * active-or-later sites and (2) avoids the mis-scoped progress banner here.
 * `'project-overview'` picks the side that never fabricates data (no
 * deep-link for now) over the side that would — see the handoff note for the
 * shared-component change (per-site status check, not page `type`) that
 * would remove this trade-off.
 *
 * Email-link-only (Task 1 decision): this route is reached solely via an
 * SF-sent email deep link to a logged-in vendor — it carries no in-app
 * entry points (see app.routes.ts + the other files this task's link sweep
 * touched), so `goBack()` always returns to the Dashboard rather than
 * assuming in-app navigation history exists.
 */
@Component({
  selector: 'project-overview-page',
  standalone: true,
  imports: [
    CommonModule,
    ProjectGroupComponent,
    AllBidsSubmittedDialogComponent,
    BidSubmittedDialogComponent,
    AtpSignAcceptedDialogComponent,
    AcceptedOfferedProposalDialogComponent,
    DeclineSuccessComponent,
    SiteVisitConfirmationDialogComponent,
  ],
  templateUrl: './project-overview-page.component.html',
  styleUrl: './project-overview-page.component.scss',
})
export class ProjectOverviewPageComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Product ordering within a site card: Invitations phase → Pending phase →
  // Active phase → Completed — mirrors the order these statuses surface across
  // the Invitations / Pending / Active tabs.
  private static readonly PHASE_RANK: Record<string, number> = {
    INVITATION: 0,
    OFFERED: 1,
    COUNTERED: 1, // Task B4 — same "pending" tier as OFFERED, awaiting staffing
    RE_OFFERED: 1, // staffing counter-back — same tier, now awaiting the PA
    SUBMITTED: 2,
    PROPOSAL: 3,
    BID_WON: 4,
    BID_LOST: 5,
    NO_RESPONSE: 6,
    ACTIVE: 7,
    REVIEW_PENDING: 8,
    REVISION_REQUESTED: 9,
    REVISION_PENDING: 10,
    IN_REVIEW: 11,
    COMPLETED: 12,
  };


  // Raw, untouched project (flat SiteDetail[] — the shape the reused
  // per-product dialogs / SiteService write calls expect) and its nested
  // Project → Site → Product view-model (what the cards render). Kept in
  // sync in fetchData().
  data: any = null;
  projectVm: ProjectVm | null = null;
  loading: boolean = true;

  // CON-154 Task 5: `childModalHeaderTitle`/`childModalBody` are the ATP
  // confirmation copy — still read by the surviving `atp-sign-accepted-dialog`
  // below (both the inline per-product sign and the banner's bulk sign now
  // route through it). The gating flags and ACTION-dialog toggles that used to
  // live here (`enableSubmitAllBids`, `enableAcceptAllOffers`, `numberOfBids`,
  // `numberOfAcceptedOffers`, `showAtp`, `allBidsActive`,
  // `acceptAllOffersActive`, `signAtpActive`, `atpDocument`,
  // `atpDocumentPath`) drove this page's own bulk buttons and dialogs, which
  // `app-project-group` now owns for every screen — removed rather than left
  // dead.
  childModalHeaderTitle: string = 'ATP Signed & Project Active';
  childModalBody: string =
    'You have signed the ATP and the project can be viewed from the Active tab.';

  // Page-hosted CONFIRMATION dialog toggles.
  bidSubmittedActive: boolean = false;
  singleBidSubmittedActive: boolean = false;
  // `isAtpSignAccepted` fires for BOTH ATP-signature paths reachable on this
  // page: a single product's inline sign inside `app-product-line`, and (as of
  // CON-154 Task 2/5) the banner's bulk "Sign All ATPs" —
  // both relay through the same `isAtpSignAcceptedModalOpen` subject/output,
  // so one dialog instance now covers what used to need two (see
  // `signedAtpSiteVisits` below).
  isAtpSignAccepted: boolean = false;
  // Site visits an ATP signature left schedulable — fed by BOTH sign paths via
  // the single `(atpSigned)` output on `<app-project-group>` (see the
  // template). Cleared in its own close handler so a second signature cannot
  // inherit the first's visits. Held on the page, never in a service — a
  // root-provided subject would keep the previous result across navigations.
  signedAtpSiteVisits: SignedAtpSiteVisit[] = [];
  isOfferAcceptedActive: boolean = false;
  // Plurality of the accept that opened the confirmation, carried on the
  // ModalService emission. This page was hard-bound to `true`, so a single
  // inline accept from a product line confirmed "All offers have been
  // accepted"; the two other list pages had the mirror-image defect.
  isOfferAcceptedMultiple: boolean = false;
  declineSuccessActive: boolean = false;
  declineBatch: BatchDeclineSummary | null = null;
  siteVisitConfirmationActive = false;
  isFixedPrice: boolean = false;
  isAtp: boolean = false;
  address: any = { streetAddress: '', cityStateZip: '' };

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private modalService: ModalService,
    private utilityService: CommonUtilityServiceService,
    private appService: AppService,
    public router: Router
  ) {}

  ngOnInit() {
    this.appService.setRedirectUrl('');

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get('id') || '';
      this.fetchData(id);
    });

    this.modalService.isSubmittedAllBidsModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => (this.bidSubmittedActive = isOpen));
    this.modalService.isSubmittedBidModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => (this.singleBidSubmittedActive = isOpen));
    this.modalService.isAtpSignAcceptedModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => (this.isAtpSignAccepted = isOpen));
    this.modalService.isDeclineModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((detail) => {
        // CON-148 #6 - outside the guard on purpose; see invitations-page for the full reasoning.
        // Guarded, this only ever wrote `true`, so the second decline never re-opened the dialog.
        // The correct shape was already two lines below, on isOfferAcceptedModalOpen.
        this.declineSuccessActive = detail.isOpen;
        if (detail.isOpen) {
          this.declineBatch = detail.batch ?? null;
          this.isFixedPrice = detail.isFixedPrice;
          this.isAtp = detail.isAtp;
          if (detail.siteDetail.site) {
            this.address = this.utilityService.formatAddress(detail.siteDetail);
          }
        }
      });
    this.modalService.isOfferAcceptedModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((offer) => {
        this.isOfferAcceptedActive = offer.open;
        this.isOfferAcceptedMultiple = offer.multiple;
      });
    this.modalService.siteVisitConfirmationModalOpen
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => (this.siteVisitConfirmationActive = isOpen));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Kept subscribed (not a one-shot take(1)) for the lifetime of the page —
  // every write the reused cards/dialogs make (per-product or, via the
  // banner, project-level) re-emits through this same ProjectService subject,
  // so the rendered cards stay live without a manual refresh.
  private fetchData(id: string): void {
    this.projectService
      .refetchData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (!result) {
          return;
        }
        const project = result.find(
          (projectDetail: any) => projectDetail.projectId === id
        );
        this.data = project && {
          ...project,
          sites: project.sites?.filter(
            (siteDetail: any) => siteDetail.proposal.status !== 'DECLINED'
          ),
        };
        this.projectVm = this.data ? toProjectVm(this.data) : null;

        // Order each site's products by phase (Invitations → Pending → Active
        // → Completed) so a multi-product site reads top-to-bottom by workflow
        // stage rather than raw data order.
        if (this.projectVm) {
          const rank = (p: any) =>
            ProjectOverviewPageComponent.PHASE_RANK[p?.status] ?? 99;
          for (const site of this.projectVm.sites) {
            site.products = [...site.products].sort(
              (a, b) => rank(a) - rank(b)
            );
          }
        }

        this.loading = false;
      });
  }

  // ---------------------------------------------------------------------------
  // Page-hosted confirmation close delegates.
  // ---------------------------------------------------------------------------

  toggleCloseSubmittedAllBidsModal(): void {
    this.modalService.toggleCloseSubmittedAllBidsModal();
  }

  toggleCloseSubmittedBidModal(): void {
    this.modalService.toggleCloseSubmittedBidModal();
  }

  toggleCloseAtpSignAcceptedModal(): void {
    this.signedAtpSiteVisits = [];
    this.modalService.toggleCloseAtpSignAcceptedModal();
  }

  toggleCloseDeclineSuccessModal(): void {
    this.modalService.toggleCloseDeclineSuccessModal();
  }

  toggleCloseOfferAcceptedModal(): void {
    this.modalService.toggleCloseOfferAcceptedModal();
  }

  toggleCloseSiteVisitConfirmationModal(): void {
    this.modalService.toggleCloseSiteVisitConfirmationModal();
  }

  // Email-link-only (Task 1 decision) — this page has no in-app entry point
  // to return to, so Back always goes to the Dashboard rather than assuming
  // in-app navigation history exists (window.history.back()).
  goBack(): void {
    this.router.navigate(['/dashboard']);
  }
}
